<script setup>
    import { onMounted, onBeforeUnmount } from 'vue';
    import ScheduleItem from './ScheduleItem.vue'
    import NoResults from './NoResults.vue'
    import ActiveFilterHeading from './ActiveFilterHeading.vue'
    import { useScheduleItemStore } from "@/stores/ScheduleItemStore"

    const store = useScheduleItemStore()

    onMounted( () => {
        // console.log( 'dispatch list mounted event' )
        const listMounted = new CustomEvent( 'listMounted', { 'detail': { 'state': '' } } )
        document.dispatchEvent( listMounted )
    })

    onBeforeUnmount( () => {
        // console.log( 'dispatch list before unmounted event' )
        const listUnMounted = new CustomEvent( 'listUnMounted', { 'detail': { 'state': '' } } )
        document.dispatchEvent( listUnMounted )
    })

    const props = defineProps({
        items: Array,
        dateTimes: Object,
        locations: Array,
        allItems: Array
    })

    const sessionsByDay = (dayAsSlug) => {
        return props.items.filter((item) => {
            return item.dayAsSlug === dayAsSlug
        })
    }

    const sessionsByLocation = (sessions, id) => {
        return sessions.filter((session) => {
            return session.item_location === id
        })   
    }

    const sessionsByTimeGroup = (sessions, day) => {
        const result = []
        const timeGroups = determineTimeGroups(day)
        timeGroups.forEach((timeGroup) => {
            const timeGroupSessions = sessions.filter((session) => {
                if ( (session.item_type === "plenary" || session.item_type === "break" || session.item_type === "social") &&
                    (session.item_starts_at === timeGroup.start && session.item_ends_at === timeGroup.end) ) {
                        return true
                    }
                else {
                    return ( session.item_starts_at >= timeGroup.start ) && ( session.item_ends_at <= timeGroup.end )
                }  
            })
            if ( timeGroupSessions.length > 0 ) {
                    result.push(timeGroupSessions)
            }
        })
        return result
    }

    const determineTimeGroups = (day) => {
        const dateTime = props.dateTimes[day]
        const plenarySessions = props.allItems.filter((item) => {
            return ( item.item_type === "plenary" || item.item_type === "break" 
                || item.item_type === "poster" || item.item_type === "social" ) 
                && item.dayAsSlug === day
        })
        const startTimes = plenarySessions.map((session) => {
            return session.item_starts_at
        })
        const endTimes = plenarySessions.map((session) => {
            return session.item_ends_at
        })
        let result = []
        endTimes.forEach((item, index) => {
            if ( ( index < startTimes.length - 1 ) && ( endTimes[index] !== startTimes[index + 1] ) ) {
                result.push({
                    start: endTimes[index],
                    end: startTimes[index + 1]
                })
            }
        })
        plenarySessions.forEach((session) => {
            result.push({
                start: session.item_starts_at,
                end: session.item_ends_at
            })
        })
        result.sort((a,b) => {
            return new Date(a.start) - new Date(b.start)
        })
        // now check if the first plenary is also start of day
        if ( result[0] && result[0].start > dateTime.day_starts_at ) {
            result.unshift({
                start: dateTime.day_starts_at,
                end: result[0].start
            })
        }
        // also check if last plenary is also end of day
        if ( result[result.length - 1] && result[result.length - 1].end < dateTime.day_ends_at ) {
            result.push({
                start: result[result.length - 1].end,
                end: dateTime.day_ends_at
            })
        }
        // console.log(result)
        for( const [ind, el] of result.entries() ) {
            if ( new Date(el.end) - new Date(el.start) <= 300000 ) {
                result.splice(ind, 1)
            }
        } 
        return result
    }

    const groupUnlistedLocations = (group) => {
        return group.filter((session) => {
            return session.item_type === "plenary" || session.item_type === "break" || session.item_type === "social"
        })
    }

    const groupListedLocations = (group) => {
        return group.filter((session) => {
            return session.item_type !== "plenary" && session.item_type !== "break" && session.item_type !== "social"
        })
    }

    const dayHasItems = (day) => {
        return props.items.filter((item) => {
            return item.dayAsSlug === day
        }).length > 0
    }
</script>

<template>
    <template v-if="store.filterActive && store.filteredItems.length > 1">
        <div class="sles__standalone-contained sles__active-filter-heading">
            <ActiveFilterHeading />
        </div>
    </template>

    <template v-for="(day, index) in Object.keys(props.dateTimes)" :key="index">
        <h3 class="sles__day__title" v-if="dayHasItems(day)">
            {{ props.dateTimes[day].dayInText }}
        </h3>
        <div class="sles__day" :id="day" :data-date="day" >

            <template v-if="store.view === 'list'">
                <div class="sles__day__grouped-items" v-for="(group, index) in sessionsByTimeGroup(sessionsByDay(day), day)" :key="index">
                    <div class="unlisted-location" v-if="groupUnlistedLocations(group).length > 0">
                        <ScheduleItem v-for="(item, index) in groupUnlistedLocations(group)" :key="index" :item="item" />
                    </div>
                    
                    <div class="sles__day__grouped-items__location" v-for="(loc, ind) in props.locations" :key="ind">
                        
                        <div :class="`sles__day__grouped-items__location__items loc-${ loc.slug }`">
                            <h4 v-if="sessionsByLocation(groupListedLocations(group), loc.id).length > 0 && ! store.filterActive" class="sles__day__grouped-items__location__title">{{ loc.loc_roomnumber && loc.loc_roomnumber !== '' ? loc.loc_roomnumber + ' - ' : '' }}{{ loc.title }}</h4>
                            <ScheduleItem v-for="(item, index) in sessionsByLocation(groupListedLocations(group), loc.id)" :key="index" :item="item" />
                        </div>
                    </div>
                </div>
            </template>

            <template v-if="store.view === 'favs'">
                <ScheduleItem v-for="(item, index) in sessionsByDay(day)" :key="index" :item="item" />
            </template>
        </div>
    </template>

    <template v-if="store.filteredItems.length < 1">
        <div class="sles__standalone-contained sles__no-results">
            <NoResults />
        </div>
    </template>
</template>
